import React, { useState, useEffect, useCallback } from 'react';
import AdminLayoutWrapper from '../../components/layout/AdminLayoutWrapper';
import DataTableLayout from '../../components/common/DataTableLayout';
import FilterPanel from '../../components/common/FilterPanel';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  MenuItem,
  Stack,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid, GridToolbarColumnsButton, GridToolbarDensitySelector, GridToolbarExport } from '@mui/x-data-grid';
import { BOARDS, ACADEMIC_YEARS, SUBJECTS, SEMESTER_TEMPLATES } from '../../config/Config';
import { createFilterChangeHandler, clearFilters, dataGridProps } from '../../utils/tableUtils';
import { fetchCurriculumTopicOptions } from '../../utils/curriculumTopics';
import api from '../../services/api';
import TopicSequencePicker from '../../components/common/TopicSequencePicker';

async function fetchPaginated(endpoint) {
  let url = endpoint;
  let all = [];
  while (url) {
    const res = await api.get(url);
    const data = res.data;
    if (Array.isArray(data)) {
      all = data;
      break;
    }
    all = all.concat(data.results || []);
    url = data.next || null;
  }
  return all;
}

export default function SemesterTemplatesPage() {
  const [rows, setRows] = useState([]);
  const [boards, setBoards] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ board: '', academic_year: '', subject: '' });
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [form, setForm] = useState({
    board: '',
    academic_year: '',
    subject: '',
    name: '',
  });
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [topicOptions, setTopicOptions] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [pendingTopicIds, setPendingTopicIds] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadReferenceData = useCallback(async () => {
    try {
      const [b, y, s] = await Promise.all([
        fetchPaginated(BOARDS),
        fetchPaginated(ACADEMIC_YEARS),
        fetchPaginated(SUBJECTS),
      ]);
      setBoards(b);
      setAcademicYears(y);
      setSubjects(s);
    } catch (err) {
      console.error(err);
      showSnackbar('Failed to load reference data', 'error');
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.board) params.append('board', filters.board);
      if (filters.academic_year) params.append('academic_year', filters.academic_year);
      if (filters.subject) params.append('subject', filters.subject);
      const q = params.toString();
      const url = q ? `${SEMESTER_TEMPLATES}?${q}` : SEMESTER_TEMPLATES;
      const list = await fetchPaginated(url);
      setRows(list);
    } catch (err) {
      console.error(err);
      showSnackbar('Failed to load semester templates', 'error');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters.board, filters.academic_year, filters.subject]);

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (!open || !form.subject || !form.board || !form.academic_year) {
      setTopicOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingTopics(true);
      try {
        const topics = await fetchCurriculumTopicOptions({
          board: form.board,
          academicYear: form.academic_year,
          subject: form.subject,
        });
        if (!cancelled) setTopicOptions(topics);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setTopicOptions([]);
          showSnackbar('Failed to load topics', 'error');
        }
      } finally {
        if (!cancelled) setLoadingTopics(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, form.subject, form.board, form.academic_year]);

  useEffect(() => {
    if (!open || !pendingTopicIds || !topicOptions.length) return;
    const ordered = pendingTopicIds
      .map((id) => topicOptions.find((t) => Number(t.id) === Number(id)))
      .filter(Boolean);
    setSelectedTopics(ordered);
    setPendingTopicIds(null);
  }, [open, pendingTopicIds, topicOptions]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this semester template?')) return;
    try {
      await api.delete(`${SEMESTER_TEMPLATES}${id}/`);
      setRows((prev) => prev.filter((r) => r.id !== id));
      showSnackbar('Template deleted');
    } catch (err) {
      console.error(err);
      showSnackbar('Failed to delete', 'error');
    }
  };

  const resetDialog = () => {
    setForm({ board: '', academic_year: '', subject: '', name: '' });
    setSelectedTopics([]);
    setTopicOptions([]);
    setPendingTopicIds(null);
    setCurrentId(null);
    setEditMode(false);
  };

  const handleClose = () => {
    setOpen(false);
    resetDialog();
  };

  const handleOpen = (row = null) => {
    if (row) {
      setEditMode(true);
      setCurrentId(row.id);
      setForm({
        board: String(row.board),
        academic_year: String(row.academic_year),
        subject: String(row.subject),
        name: row.name || '',
      });
      setSelectedTopics([]);
      setPendingTopicIds(row.topic_ids && row.topic_ids.length ? [...row.topic_ids] : null);
    } else {
      resetDialog();
      setOpen(true);
      return;
    }
    setOpen(true);
  };

  const subjectScopeReady = !!(form.board && form.academic_year && form.subject);

  const handleSubmit = async () => {
    if (!form.name?.trim()) {
      showSnackbar('Please enter a semester name', 'error');
      return;
    }
    if (!form.board || !form.academic_year || !form.subject) {
      showSnackbar('Board, academic year, and subject are required', 'error');
      return;
    }
    if (!selectedTopics.length) {
      showSnackbar('Add at least one topic', 'error');
      return;
    }
    const payload = {
      board: Number(form.board),
      academic_year: Number(form.academic_year),
      subject: Number(form.subject),
      name: form.name.trim(),
      topic_ids: selectedTopics.map((t) => t.id).filter((id) => id != null),
    };
    setSaving(true);
    try {
      if (editMode) {
        await api.patch(`${SEMESTER_TEMPLATES}${currentId}/`, payload);
        showSnackbar('Template updated');
      } else {
        await api.post(SEMESTER_TEMPLATES, payload);
        showSnackbar('Template created');
      }
      handleClose();
      loadTemplates();
    } catch (err) {
      console.error(err);
      const msg =
        err.response?.data?.name?.[0] ||
        err.response?.data?.non_field_errors?.[0] ||
        (typeof err.response?.data === 'string' ? err.response.data : null) ||
        'Failed to save template';
      showSnackbar(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleFilterChange = createFilterChangeHandler(setFilters);
  const handleClearFilters = () => clearFilters(setFilters, { board: '', academic_year: '', subject: '' });

  const filterConfig = [
    {
      field: 'board',
      label: 'Board',
      type: 'select',
      options: boards.map((b) => ({ value: String(b.id), label: b.name })),
    },
    {
      field: 'academic_year',
      label: 'Academic year',
      type: 'select',
      options: academicYears.map((y) => ({ value: String(y.id), label: String(y.year) })),
    },
    {
      field: 'subject',
      label: 'Subject',
      type: 'select',
      options: subjects.map((s) => ({ value: String(s.id), label: s.name })),
    },
  ];

  const columns = [
    {
      field: 'board_name',
      headerName: 'Board',
      flex: 1,
      renderCell: (p) => <Chip label={p.value || '—'} size="small" variant="outlined" color="primary" />,
    },
    {
      field: 'academic_year_display',
      headerName: 'Year',
      flex: 0.6,
    },
    {
      field: 'subject_name',
      headerName: 'Subject',
      flex: 1,
    },
    {
      field: 'name',
      headerName: 'Semester',
      flex: 1,
    },
    {
      field: 'topic_count',
      headerName: 'Topics',
      flex: 0.5,
      type: 'number',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleOpen(params.row)} sx={{ p: 0.5 }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.id)}
              sx={{ p: 0.5, color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const actionButtons = [
    {
      label: 'New template',
      icon: <AddIcon />,
      variant: 'contained',
      onClick: () => handleOpen(null),
    },
  ];

  return (
    <AdminLayoutWrapper>
      <DataTableLayout
        title="Semester templates"
        actionButtons={actionButtons}
        filterPanel={
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterConfig={filterConfig}
          />
        }
        loading={loading}
        isEmpty={!loading && rows.length === 0}
        emptyStateProps={{
          title: 'No semester templates',
          description: 'Create a template for a board, academic year, and subject, then order topics.',
          icon: <AddIcon sx={{ fontSize: 40, color: 'text.secondary' }} />,
        }}
        mobileFilterOpen={mobileFilterOpen}
        onMobileFilterClose={() => setMobileFilterOpen(!mobileFilterOpen)}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          {...dataGridProps}
          components={{
            Toolbar: () => (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                  p: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GridToolbarColumnsButton sx={{ color: 'primary.main', '&:hover': { backgroundColor: 'primary.50' } }} />
                  <GridToolbarDensitySelector sx={{ color: 'primary.main', '&:hover': { backgroundColor: 'primary.50' } }} />
                  <GridToolbarExport sx={{ color: 'primary.main', '&:hover': { backgroundColor: 'primary.50' } }} />
                </Box>
              </Box>
            ),
          }}
        />
      </DataTableLayout>

      <Dialog open={open} onClose={saving ? undefined : handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit semester template' : 'New semester template'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Semester name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              required
              placeholder="e.g. Semester 1"
            />
            <TextField
              select
              label="Board"
              value={form.board}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({ ...f, board: v }));
                setSelectedTopics([]);
                setPendingTopicIds(null);
              }}
              fullWidth
              required
            >
              {boards.map((b) => (
                <MenuItem key={b.id} value={String(b.id)}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Academic year"
              value={form.academic_year}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({ ...f, academic_year: v }));
                setSelectedTopics([]);
                setPendingTopicIds(null);
              }}
              fullWidth
              required
            >
              {academicYears.map((y) => (
                <MenuItem key={y.id} value={String(y.id)}>
                  {y.year}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Subject"
              value={form.subject}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({ ...f, subject: v }));
                setSelectedTopics([]);
                setPendingTopicIds(null);
              }}
              fullWidth
              required
            >
              {subjects.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
            <TopicSequencePicker
              topicOptions={topicOptions}
              value={selectedTopics}
              onChange={setSelectedTopics}
              disabled={saving}
              loading={loadingTopics}
              subjectSelected={subjectScopeReady}
              showQuestionCount={false}
              helperTextWhenNoSubject="Select board, academic year, and subject first"
              helperTextDefault={
                subjectScopeReady && !loadingTopics && topicOptions.length === 0
                  ? 'No topics in curriculum for this board, year, and subject. Add mappings in Manage Curriculum.'
                  : 'Only curriculum topics are listed. Use arrows below to change 1st, 2nd, … order'
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {editMode ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminLayoutWrapper>
  );
}
